// =============================================================================
// ui/tabs/DiscordWebhooks.tsx — Settings → App → Discord.
// -----------------------------------------------------------------------------
// 🔴 THE GAP THIS CLOSES: the "Post to Discord" button on a trade was already
// built, already correct, and could never appear — it shows only once a webhook
// URL is stored, and nothing in this client could store one. Code that reads as
// finished and is unreachable is worse than code that is missing.
//
// ── 🚨 THE TEST IS THE SAVE ──
//
// There is no Save button, and that is the old client's design rather than a
// shortcut. A stored webhook that has never posted looks exactly like a working
// one, until the trader shares a trade and nothing arrives in the channel. So
// the only way a URL reaches the database is by successfully posting to it.
//
// ⚠ CLEARING A SLOT IS THE EXCEPTION, and it has to be: an empty box cannot be
// tested, and a trader who has rotated their webhook needs a way to stop this
// app posting to a channel it should no longer reach. Clearing writes
// immediately.
// =============================================================================

import { useState } from 'react';
import { usePreferences } from '../state/preferences';
import { pingDiscordWebhook } from '../../io/discord/testWebhook';
import {
  WEBHOOK_PURPOSES, isDiscordWebhookUrl, setWebhook, webhookFor,
  type WebhookKey,
} from '../../selectors/webhooks';

type Note = { key: WebhookKey; tone: 'ok' | 'bad'; text: string };

export function DiscordWebhooks() {
  const { prefs, update } = usePreferences();
  const stored = prefs?.webhooks ?? {};

  /**
   * What is typed, per slot, before it has been proved.
   *
   * ⚠ SEPARATE FROM `stored`, deliberately. The box has to hold an unsaved URL
   * while the trader tests it — if it read straight from preferences, every
   * keystroke would either write an untested URL or be thrown away on re-render.
   */
  const [draft, setDraft] = useState<Partial<Record<WebhookKey, string>>>({});
  const [busy, setBusy] = useState<WebhookKey | null>(null);
  const [note, setNote] = useState<Note | null>(null);

  const valueFor = (key: WebhookKey): string =>
    draft[key] ?? webhookFor(stored, key);

  const test = async (key: WebhookKey, label: string): Promise<void> => {
    const url = valueFor(key).trim();
    if (!url) {
      setNote({ key, tone: 'bad', text: `Paste the ${label} webhook URL first.` });
      return;
    }
    // ⚠ SHAPE FIRST, so an obviously-wrong paste fails instantly and locally
    // rather than as a network error the reader has to interpret.
    if (!isDiscordWebhookUrl(url)) {
      setNote({
        key, tone: 'bad',
        text: 'That is not a Discord webhook URL. In Discord: Channel settings '
          + '→ Integrations → Webhooks → Copy Webhook URL.',
      });
      return;
    }

    setBusy(key);
    setNote(null);
    const res = await pingDiscordWebhook(url, label);
    setBusy(null);

    if (!res.ok) {
      // 🚨 NOT SAVED. The whole point of testing before storing.
      setNote({ key, tone: 'bad', text: res.error ?? 'The test failed.' });
      return;
    }

    const next = setWebhook(stored, key, url);
    if (next !== stored) await update({ webhooks: next });
    setNote({
      key, tone: 'ok',
      text: `Posted. ${label} is connected — check the channel, and the Post to `
        + 'Discord button now appears where it is triggered.',
    });
  };

  const clear = async (key: WebhookKey): Promise<void> => {
    const next = setWebhook(stored, key, '');
    setDraft((d) => ({ ...d, [key]: '' }));
    if (next !== stored) await update({ webhooks: next });
    setNote({ key, tone: 'ok', text: 'Cleared. This app will not post there again.' });
  };

  return (
    <section className="card" data-testid="discord-webhooks">
      <h3>Discord</h3>
      <p className="muted">
        One webhook per purpose, so different content goes to different channels.
        {/* ⚠ THE OLD CLIENT SAYS THE URL "stays local". It does not — here it is
            stored with your other settings on the server, and repeating a
            reassurance the storage model contradicts is how a privacy claim
            becomes a lie. */}
        {' '}A webhook URL is a credential: anyone who has it can post to that
        channel until you regenerate it in Discord. It is stored with your other
        settings.
      </p>

      {WEBHOOK_PURPOSES.map((p) => {
        const saved = webhookFor(stored, p.key);
        const value = valueFor(p.key);
        const dirty = value.trim() !== saved;
        const slotNote = note && note.key === p.key ? note : null;
        return (
          <div className="wh-slot" key={p.key}>
            <div className="wh-head">
              <b>{p.label}</b>
              {saved && !dirty ? <span className="wh-on">connected</span> : null}
            </div>
            <p className="muted">{p.description}</p>
            <div className="wh-row">
              {/* ⚠ A PASSWORD FIELD because the URL IS the credential — not for
                  secrecy theatre, but so it is not read off a shared screen or a
                  screenshot of this page. */}
              <input
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="https://discord.com/api/webhooks/…"
                aria-label={`${p.label} webhook URL`}
                value={value}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, [p.key]: e.target.value }));
                  setNote(null);
                }}
              />
              <button
                type="button" className="btn"
                disabled={busy !== null}
                onClick={() => { void test(p.key, p.label); }}
              >
                {busy === p.key ? 'Testing…' : 'Test & save'}
              </button>
              {saved ? (
                <button
                  type="button" className="btn"
                  disabled={busy !== null}
                  onClick={() => { void clear(p.key); }}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {/* ⚠ SAYS SO WHEN THE BOX AND THE DATABASE DISAGREE. Without this a
                trader who pastes a new URL and never presses Test believes it is
                stored, because the box shows what they typed. */}
            {dirty && value.trim() ? (
              <p className="muted wh-note">Not saved yet — press Test &amp; save.</p>
            ) : null}
            {slotNote ? (
              <p
                className={slotNote.tone === 'ok' ? 'muted wh-note' : 'wh-note wh-bad'}
                role={slotNote.tone === 'ok' ? 'status' : 'alert'}
              >
                {slotNote.text}
              </p>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
